import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import {
  WEEKDAYS,
  dayIntervals,
  openingStatus,
  parseOpeningHours,
  type OpeningHours,
  type OpeningStatus,
} from "@majourneys/shared";
import { BreathingDot } from "../../internal-ui/breathing-dot";
import { PW, pewterPanelApproved } from "../../internal-ui/pewter-shell";
import { EngravedPanel } from "./engraved";
import { useVenueClock } from "./use-venue-clock";
import { venueWeekday, weekdayName } from "./weekday";

export interface OpeningHoursCardProps {
  hours: OpeningHours | undefined;
  /** Pins the clock (tests). */
  now?: Date;
  className?: string;
}

/* Same dot colours as the open-now pill, so the two never disagree. */
const DOT = { open: "#2F855A", closing_soon: "#C27A0E", closed: "#8A8CA3" } as const;

/**
 * The status in the card's header: the open-now pill's rules
 * (`openingStatus` from shared, the same labels), cut as a white control
 * with one hairline so it sits on the pewter panel like the lab's "#06".
 */
function StatusPill({ status }: { status: OpeningStatus }) {
  const { t } = useTranslation();
  const label =
    status.state === "open"
      ? t("activity.restaurant.open_now")
      : status.state === "closing_soon"
      ? t("activity.restaurant.closes_soon")
      : t("activity.restaurant.closed");
  return (
    <span
      data-testid="opening-hours-status"
      data-state={status.state}
      className="inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 font-sans text-[12.5px] font-semibold leading-none"
      style={{ background: PW.controlFill, boxShadow: `0 0 0 1px ${PW.controlEdge}`, color: PW.navy }}
    >
      <BreathingDot color={DOT[status.state]} still={status.state === "closed"} />
      {label}
    </span>
  );
}

/**
 * The week, Monday first (the Moroccan and European convention), one row per
 * day. An overnight interval reads as authored, "18:30 to 02:00", on the day
 * it opens: that is how venues print it and how guests say it. A day with a
 * lunch and a dinner service reads "12:00 to 15:00 · 19:00 to 23:30" on one
 * row, the gap left implicit the way a printed menu card shows it.
 *
 * "#06" from the pewter lab (owner pick): status on top, today's row lifted
 * out as a white control row, closed days dimmed. Today's row and the status
 * are only shown after mount, on the venue's clock, for the same hydration
 * reason as the open-now pill.
 */
export function OpeningHoursCard({ hours: stored, now, className }: OpeningHoursCardProps) {
  const { t, i18n } = useTranslation();
  const clock = useVenueClock(now);
  // A malformed week renders nothing rather than "undefined to undefined".
  const hours = parseOpeningHours(stored);
  if (!hours) return null;
  const today = clock ? venueWeekday(clock) : null;
  const status = clock ? openingStatus(hours, clock) : null;

  return (
    <EngravedPanel
      aria-labelledby="opening-hours-title"
      className={`p-3.5 md:p-4 ${className ?? ""}`}
      style={pewterPanelApproved(22)}
    >
      <div className="mb-2 flex min-h-[32px] flex-wrap items-center justify-between gap-2 px-1.5">
        <h2
          id="opening-hours-title"
          className="flex items-center gap-2 font-heading text-[20px] font-bold md:text-[22px]"
          style={{ color: PW.navy }}
        >
          <Icon name="ri-time-line" className="text-[18px]" aria-hidden="true" />
          {t("activity.restaurant.opening_hours")}
        </h2>
        {status && <StatusPill status={status} />}
      </div>
      <ul className="flex flex-col" data-testid="opening-hours">
        {WEEKDAYS.map((day) => {
          const services = dayIntervals(hours[day]);
          const split = services.length > 1;
          const closed = services.length === 0;
          const isToday = day === today;
          const name = upperFirst(weekdayName(day, i18n.language));
          return (
            <li
              key={day}
              data-day={day}
              data-closed={closed || undefined}
              aria-current={isToday ? "date" : undefined}
              className={`flex min-h-[36px] items-center justify-between gap-3 rounded-[10px] px-2.5 font-sans text-[14px] tabular-nums ${
                isToday ? "font-semibold" : ""
              }`}
              style={
                isToday
                  ? { background: PW.controlFill, boxShadow: `0 0 0 1px ${PW.controlEdge}`, color: PW.navy }
                  : { color: PW.muted }
              }
            >
              {/* Two services are the long row: the day name keeps its width and
                  the hours wrap between services on a narrow phone instead. */}
              <span className={`break-words ${split ? "shrink-0" : "min-w-0"}`}>
                {isToday ? t("activity.restaurant.today_row", { day: name }) : name}
              </span>
              <span
                className={split ? "min-w-0 text-end" : "shrink-0"}
                // A closed day is dimmed on its hours, not its name, so the
                // day stays legible at the muted ink.
                style={closed && !isToday ? { opacity: 0.7 } : undefined}
                dir="auto"
              >
                {closed
                  ? t("activity.restaurant.closed")
                  : services
                      .map((s) => t("activity.restaurant.hours_range", { open: s.open, close: s.close }))
                      .reduce((first, second) =>
                        t("activity.restaurant.hours_services", { first, second }),
                      )}
              </span>
            </li>
          );
        })}
      </ul>
    </EngravedPanel>
  );
}

/* Intl gives "lundi" in French; a row label starts with a capital. */
function upperFirst(s: string): string {
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
}
